/**
 * The dashboard's tabs, in the order the tab bar shows them.
 *
 * This file is a plain list. A tab's logic lives in the tab; which tabs exist
 * and in what order lives here and nowhere else. To add a tab, add one entry.
 *
 * A tab imports `DashboardTabProps` from here with `import type`, so the tab
 * and this file do not import each other at run time.
 */
import type { ReactNode } from 'react';
import type { ClientSnapshot, DashboardView } from '@/types/fleetAnalytics';
import type { ContractOf, StackContract } from '../contracts';
import type { FleetData, Freshness } from '../fleetData';
import type { FleetViewDef, WindowState } from '../shared';
import { Agents } from './Agents';
import { Apps } from './Apps';
import { Clients } from './Clients';
import { Cost } from './Cost';
import { Infrastructure } from './Infrastructure';
import { Overview } from './Overview';
import { People } from './People';
import { Setup } from './Setup';
import { Usage } from './Usage';

/** What is selected in the sidebar, in the terms a tab needs. */
export interface Selection {
  /** `_ARCANUM`, a client name, or `_infra:<alias>`. */
  id: string;
  /** `view`: a fleet-wide sum. `stack`: one client. `account`: one AWS account with no Numa stack. */
  kind: 'view' | 'stack' | 'account';
  /** The name on screen. */
  label: string;
  /** One sentence: what a view sums, or a stack's region and organisation. */
  description: string;
  /**
   * The stacks and accounts behind the selection, as the lists show them.
   * Inactive stacks a view hides are not in it. One entry for a stack or an
   * account. Feed the row selectors.
   */
  stacks: ClientSnapshot[];
  /** Hidden Inactive stacks whose spend is in the view's spend figures. Empty for one stack or account. */
  costOnly: ClientSnapshot[];
  /** Each stack's contract, from the portal's live record. Pass it to the row selectors. */
  contractOf: ContractOf;
  /** Every client tagged Inactive, shown or hidden. */
  inactive: ReadonlySet<string>;
  /** One stack: its contract. Pass it to `selectCredits`. */
  contract?: StackContract;
  /** The stale or failed stacks among them, oldest first. */
  stale: Array<{ clientName: string } & Freshness>;
  /** The day the selection's numbers run up to, `YYYY-MM-DD`. Before today when the nightly update is behind. */
  asOf: string;
  /** Which fleet-wide view. `kind: 'view'` only. */
  viewKind?: FleetViewDef['kind'];
}

export interface DashboardTabProps {
  /** The selection as one object: a summed view, or one stack. Feed the totals selectors. */
  data: DashboardView;
  /** The time window. Both days inclusive, UTC. */
  window: WindowState;
  /** Every stack and account in the fleet, whatever is selected. */
  snapshots: ClientSnapshot[];
  /**
   * The fleet's data, scoped to the selection. For one stack it reports a
   * tier ready when that stack's complete snapshot is loaded, and loads that
   * snapshot in place of the tier file. Pass it to `<NeedsTier>`.
   */
  fleet: FleetData;
  /** The same, not scoped: tiers for every row of `snapshots`. For compare and the fleet map. */
  wholeFleet: FleetData;
  selection: Selection;
  /**
   * Select something in the sidebar by its id. Any selection id works: a
   * stack (`moira-shire-council`), an infrastructure account
   * (`_infra:arcanum-dev`) or a fleet view (`_ARCANUM_INFRA`). `null` goes
   * back to the default view.
   */
  onSelectClient: (id: string | null) => void;
  /** Open another tab by its `key`. A key that is not offered for the selection opens the first tab. */
  onSelectTab?: (key: string) => void;
}

export interface DashboardTab {
  key: string;
  /** The words on the tab. Sentence case. */
  label: string;
  /** Is the tab offered for this selection? */
  show: (selection: Selection) => boolean;
  render: (props: DashboardTabProps) => ReactNode;
}

/** A selection with people in it: a stack, or any view but the infrastructure accounts. */
const hasPeople = (s: Selection) => s.kind === 'stack' || (s.kind === 'view' && s.viewKind !== 'arcanum-infra');
/** A selection that is AWS cost and nothing else: one infrastructure account, or the view of them all. */
const costOnly = (s: Selection) => !hasPeople(s);
/** A view that holds stacks to set side by side. One stack has nothing to compare with. */
const hasStacks = (s: Selection) => s.kind === 'view' && s.viewKind !== 'arcanum-infra';

/**
 * Each tab asks for the heavier data it needs itself, part by part, so a tab
 * draws its figures at once and no entry here waits on a tier.
 */
export const DASHBOARD_TABS: DashboardTab[] = [
  { key: 'overview', label: 'Overview', show: hasPeople, render: (p) => <Overview {...p} /> },
  { key: 'cost', label: 'Cost', show: hasPeople, render: (p) => <Cost {...p} /> },
  { key: 'usage', label: 'Usage', show: hasPeople, render: (p) => <Usage {...p} /> },
  { key: 'people', label: 'People', show: hasPeople, render: (p) => <People {...p} /> },
  { key: 'agents', label: 'Agents and automations', show: hasPeople, render: (p) => <Agents {...p} /> },
  { key: 'apps', label: 'Apps', show: hasPeople, render: (p) => <Apps {...p} /> },
  { key: 'clients', label: 'Clients', show: hasStacks, render: (p) => <Clients {...p} /> },
  { key: 'setup', label: 'Setup', show: hasPeople, render: (p) => <Setup {...p} /> },
  { key: 'infrastructure', label: 'Infrastructure', show: costOnly, render: (p) => <Infrastructure {...p} /> },
];
